import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { provideRouter, withRouterConfig } from '@angular/router';
import { provideTransloco } from '@jsverse/transloco';
import { routes } from './app.routes';
import { ConfigService } from './services/config.service';
import { BaseConfig } from './types/base-config';
import { initializeTranslations } from './utils/translation-initializer';
import { TranslocoHttpLoader } from './utils/transloco-loader';
import { ApiModule, Configuration } from './core/api';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { tokenInterceptor } from './core/interceptors/token.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { STORAGE_KEY } from './services/storage.service';
import { AuthService } from './services/auth.service';
import { registerLocaleData } from '@angular/common';
import localeDE from '@angular/common/locales/de';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { UserService } from './services/user.service';
import { ConfigPreloadService } from './services/config-preload.service';
import { LocalStorageService } from './services/local-storage.service';

const REFRESH_SUBSCRIPTION_AFTER_STRIPE_KEY = 'refreshSubscriptionAfterStripe';

export async function initializationFactory() {
  // Create preload service first so it subscribes to auth; it triggers non-blocking load of notification options and password policy
  inject(ConfigPreloadService);

  const configService = inject(ConfigService);
  const authService = inject(AuthService);
  const userService = inject(UserService);
  const localStorageService = inject(LocalStorageService);

  // register ag-grid
  ModuleRegistry.registerModules([AllCommunityModule]);

  // register locale for angulars date pipes
  registerLocaleData(localeDE);

  // Load config FIRST before any other initialization
  await configService.init(new BaseConfig());
  await authService.initializeAndRestore();

  // Load user and tax office data if authentication was successful
  if (!authService.isTokenExpired()) {
    const shouldRefreshSubscription = localStorageService.get(REFRESH_SUBSCRIPTION_AFTER_STRIPE_KEY) === '1';
    if (shouldRefreshSubscription) {
      localStorageService.remove(REFRESH_SUBSCRIPTION_AFTER_STRIPE_KEY);

      // Returning from Stripe, so the subscription may just have been paid. The current access token still
      // carries the stale SubscriptionBlocked claim, and the API refuses every request while it is set.
      // Force a token exchange first so the user comes back unblocked instead of bouncing off 402s.
      await authService.refreshTokenOnFailure();

      await Promise.all([userService.loadCurrentUser(), userService.loadTaxOfficeData(), userService.loadSubscriptionData(true)]);
    } else {
      await userService.initialize();
    }
  }
}

/**
 * Configures the `ApiModule` generated with OpenApi Generator.
 */
export function apiConfigFactory(): Configuration {
  const config = new Configuration({});
  // Override the basePath property with a getter that always returns the latest value
  Object.defineProperty(config, 'basePath', {
    get: () => {
      const api = ConfigService.getConfig().api;
      return api;
    },
    configurable: true,
    enumerable: true
  });
  return config;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withRouterConfig({
        paramsInheritanceStrategy: 'always'
      })
    ),
    provideAppInitializer(initializationFactory),
    provideHttpClient(withInterceptors([tokenInterceptor, errorInterceptor])),
    provideAngularSvgIcon(),
    provideTransloco({
      config: {
        availableLangs: ['de'],
        defaultLang: 'de',
        fallbackLang: 'de',

        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode()
      },
      loader: TranslocoHttpLoader
    }),
    provideAppInitializer(initializeTranslations),
    {
      provide: STORAGE_KEY,
      useValue: 'tx'
    },
    {
      provide: LOCALE_ID,
      useValue: 'de-DE'
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler()
    },
    {
      provide: Sentry.TraceService,
      deps: [Router]
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true
    },
    ApiModule.forRoot(apiConfigFactory).providers || []
  ]
};
